import type { ColumnDef } from '@tanstack/react-table'
import { AlertTriangle, Banknote, MoreHorizontal, X } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { FormEvent, ReactNode } from 'react'
import { toast } from 'sonner'
import { useTranslate } from 'shared/lib/i18n'
import type { RouterOutputs } from 'shared/api'
import { cashCancelResultKey, trpcClient } from 'shared/api'
import { DataTable } from 'shared/components/data-table/data-table'
import { PageFrame } from 'shared/components/page-frame'
import { ScanInput } from 'shared/components/scan-input'
import { Stat, Stats } from 'shared/components/section'
import { ToneBanner, type Tone } from 'shared/components/tone-banner'
import { Alert, AlertDescription, AlertTitle } from 'shared/components/ui/alert'
import {
	AlertDialog,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from 'shared/components/ui/alert-dialog'
import { Badge } from 'shared/components/ui/badge'
import { Button } from 'shared/components/ui/button'
import { Checkbox } from 'shared/components/ui/checkbox'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from 'shared/components/ui/dropdown-menu'
import { Input } from 'shared/components/ui/input'
import { useDataTable } from 'shared/hooks/use-data-table'
import { playTone } from 'shared/lib/tone-sound'

/**
 * Handover (Выдача) — the counter flow (spec §15, ADR-0031/0032). A person
 * arrives with a code and leaves with some or all of their parcels. The operator
 * types or scans the code the person shows — whoever shows it is the Recipient
 * (CONTEXT — Identification, by code) — and the server lists everything ready for
 * that Recipient at this Point. A code two partners share comes back ambiguous and
 * the operator chooses the partner; the screen never guesses.
 *
 * The screen is a conversation at the counter, in the hub's look: the Recipient's
 * code and the parcel scan side by side on top; once the Recipient is found, the
 * summary and the acts, the answer to the last scan or act (`ToneBanner`, sounded in
 * its tone, its stopwatch running), and their parcels under it.
 *
 * Rows are then checked **only by scanning each parcel's code**, and every scan is
 * checked by the server (`checkScan`): this Recipient's parcel is checked, someone
 * else's is a warning. A click on a checked row's box unchecks it — a click never
 * checks one. **Принять наличные** — or **Uzum**, offered only when the partner is
 * 139 Express (`uzumAccepted`) — takes payment for the checked rows and **Выдать**
 * hands them over, each in ONE request by the codes scanned for them, all written
 * or none (ADR-0049) — so a Recipient may take and pay for part of their parcels. In «⋯»: **Снять
 * выбор** clears the selection; with a paid row selected, **Отменить оплату**
 * corrects our own counter payment for it (ADR-0020), asked first — the server
 * refuses it outside the operator's own Shift or when the Cashbox cannot give the
 * money back, and the screen names why.
 *
 * The acts stay the server's: the amount is the server's (`amountOwed`, already 0
 * when the partner's Recipient does not pay), Cash sends no amount, and `paid?`
 * comes with each row. The calls are injected so they are the stubbed tRPC seam in
 * tests (spec Testing Decisions). Every operator-facing string goes through the
 * key-based i18n lookup; Russian is the only shipped language.
 *
 * **By document** (ADR-0031): neither partner identifies by document, so the counter
 * offers no document field; a by-document partner's code is answered as such.
 *
 * **In the Shift** (ADR-0035): every handover is done in the operator's own open
 * Shift. When it is not open the screen says so, with the way to the Shift.
 *
 * **A held Parcel** (ADR-0033, ticket 51): a Parcel whose storage ran out and that
 * waits for its partner's decision, or that the partner decided to take back or to
 * dispose of, is listed for the Recipient and named so (`hold`, the server's one
 * rule), but it is not ready for handover: it is not counted ready, its scan checks
 * no row and adds nothing to the total. The server still refuses it on "Выдать".
 */

type LookupResult = RouterOutputs['handover']['ReadyForBearerCode']
type HandOverResult = RouterOutputs['handover']['HandOver']
type HandoverRefusal = Extract<HandOverResult, { outcome: 'refused' }>['reason']
/** The answer to the one payment or the one handover of all the ready parcels (ADR-0049). */
type VisitResult = RouterOutputs['handover']['HandOverAll']
type VisitParcelRefusal = Extract<VisitResult, { outcome: 'parcels_refused' }>['parcels'][number]['reason']
type ScanCheckResult = RouterOutputs['handover']['CheckScan']
type CancelResult = RouterOutputs['payments']['CancelCollected']
type ReadyParcel = Extract<LookupResult, { outcome: 'ready' }>['parcels'][number]
type StorageHold = NonNullable<ReadyParcel['hold']>
type AmbiguousPartner = Extract<LookupResult, { outcome: 'ambiguous' }>['partners'][number]
type Recipient = Extract<LookupResult, { outcome: 'ready' }>['recipient']
type ScanRecipient = { partnerId: string; clientRef: string }
type ScanInput = { recipient: ScanRecipient; code: string }
type PaymentMethod = 'cash' | 'uzum'
type PayAllInput = {
	recipient: ScanRecipient
	codes: string[]
	method?: PaymentMethod
	idempotencyKey: string
}
type HandOverAllInput = {
	recipient: ScanRecipient
	items: { code: string; damagedAccepted?: boolean }[]
}
type CancelInput = { parcelId: string; idempotencyKey: string }
/** The operator's Shift at this Point, as far as the counter needs it. */
type ShiftState = { open: boolean; mine: boolean }

/**
 * The answer under the parcel scan — to the last scan or the last act: a parcel
 * checked, a scan not accepted (its warning key), or what Cash / Выдать / the
 * cancellation did (one line per parcel that did not go as asked).
 */
type Answer =
	| { kind: 'checked'; code: string; parcelId: string }
	| { kind: 'warn'; code: string; warnKey: string }
	| { kind: 'result'; tone: Tone; title: string; lines: string[] }

// The server serves the shelf of the operator's own Point, from their binding —
// never from the request (ADR-0029).
async function lookupViaTrpc(code: string, partnerId?: string): Promise<LookupResult> {
	return trpcClient.handover.ReadyForBearerCode.query({ code, partnerId })
}

async function shiftStateViaTrpc(): Promise<ShiftState> {
	const current = await trpcClient.shifts.Current.query({})
	return { open: current.shift !== null, mine: current.mine }
}

async function checkScanViaTrpc(input: ScanInput): Promise<ScanCheckResult> {
	return trpcClient.handover.CheckScan.query(input)
}

async function handOverAllViaTrpc(input: HandOverAllInput): Promise<VisitResult> {
	return trpcClient.handover.HandOverAll.mutate(input)
}

async function payAllViaTrpc(input: PayAllInput): Promise<VisitResult> {
	return trpcClient.handover.PayAll.mutate(input)
}

async function cancelViaTrpc(input: CancelInput): Promise<CancelResult> {
	return trpcClient.payments.CancelCollected.mutate(input)
}

const newCommandKey = () =>
	globalThis.crypto?.randomUUID?.() ?? `payment-${Date.now()}-${Math.random().toString(36).slice(2)}`

/** The operator-facing warning for a scan the server did not accept, or null. */
function scanWarnKey(outcome: ScanCheckResult['outcome']): string | null {
	switch (outcome) {
		case 'this_recipient':
			return null
		case 'other_recipient':
			return 'handover.scanOtherRecipient'
		case 'identify_by_document':
			return 'handover.identifyByDocumentScan'
		case 'not_on_shelf':
			return 'handover.scanNotOnShelf'
		case 'not_found':
			return 'handover.scanNotFound'
	}
}

/** The words for a handover the server refused before anything was written. */
function refusalKey(reason: HandoverRefusal): string {
	switch (reason) {
		case 'no_open_shift_to_hand_over':
			return 'handover.noOpenShift'
		case 'not_shift_operator_to_hand_over':
			return 'handover.notShiftOperator'
		// The counter has no document check any more; the server's refusal reads as its lookup's.
		case 'document_not_checked':
		case 'identify_by_document':
			return 'handover.identifyByDocumentScan'
		// A partner decision on its expired storage holds it on the shelf (ticket 40), and
		// so does a Parcel found after it was declared lost, until its partner answers (ticket 43).
		case 'awaiting_partner_decision':
		case 'awaiting_found_decision':
		case 'decided_return':
		case 'decided_dispose':
			return `handover.block_${reason}`
		// The return flow (ticket 42): a return goes back; a damaged one needs the Recipient's consent.
		case 'in_return_flow':
		case 'damaged_not_accepted':
			return `handover.block_${reason}`
	}
}

/** The words for a parcel that stopped the visit's payment or handover (ADR-0049). */
function visitRefusalKey(reason: VisitParcelRefusal): string {
	switch (reason) {
		case 'other_recipient':
			return 'handover.scanOtherRecipient'
		case 'not_on_shelf':
		case 'not_on_shelf_to_pay':
			return 'handover.scanNotOnShelf'
		case 'not_found':
		case 'parcel_not_found':
			return 'handover.scanNotFound'
		case 'not_paid':
			return 'handover.blockNotPaid'
		case 'uzum_not_accepted':
			return 'payment.uzumNotAccepted'
		case 'return_not_paid':
			return 'handover.block_in_return_flow'
		default:
			return refusalKey(reason)
	}
}

/** The words naming a held Parcel in its row (ticket 51). */
function holdKey(hold: StorageHold): string {
	return `handover.hold_${hold}`
}

const SHIFT_REFUSALS: readonly HandoverRefusal[] = [
	'no_open_shift_to_hand_over',
	'not_shift_operator_to_hand_over',
]

export interface HandoverProps {
	/** The way to the Shift section, shown beside "open a Shift" (a router link in the app). */
	shiftLink?: ReactNode
	/** What the page shows under the counter — the Point's double payments in the app. */
	footer?: ReactNode
	/** Test seam: whether the operator's own Shift is open at this Point. */
	shiftState?: () => Promise<ShiftState>
	/** Test seam: the Recipient lookup by code. Defaults to the real tRPC client. */
	lookup?: (code: string, partnerId?: string) => Promise<LookupResult>
	/** Test seam: the server's check of one parcel scan. */
	checkScan?: (input: ScanInput) => Promise<ScanCheckResult>
	/** Test seam: the one handover of every ready parcel. */
	handOverAll?: (input: HandOverAllInput) => Promise<VisitResult>
	/** Test seam: the one payment for every ready parcel. */
	payAll?: (input: PayAllInput) => Promise<VisitResult>
	/** Test seam: the counter-payment cancellation. */
	cancelCollected?: (input: CancelInput) => Promise<CancelResult>
	/** Called after cash was cancelled here — the Point's double payments are read again. */
	onCashCancelled?: () => void
}

type Lookup = 'idle' | 'not_found' | 'wrong_point' | 'ambiguous' | 'identify_by_document' | 'ready'

/** The tone of a lookup that found no Recipient. */
const LOOKUP_TONE: Record<Exclude<Lookup, 'idle' | 'ready'>, Tone> = {
	not_found: 'attention',
	identify_by_document: 'attention',
	wrong_point: 'refused',
	ambiguous: 'attention',
}

/** A sum as the counter reads it: digits grouped, the currency named once in its label. */
function sum(amount: number): string {
	return amount.toLocaleString('ru-RU')
}

/** A weight in kilograms, as the counter reads it. */
function kg(weight: number): string {
	return weight.toLocaleString('ru-RU', { maximumFractionDigits: 3 })
}

function firstTrackingCode(parcel: ReadyParcel): string {
	return parcel.resolvingKeys[0]?.trackingCode ?? parcel.parcelId
}

export function Handover({
	shiftLink,
	footer,
	shiftState = shiftStateViaTrpc,
	lookup = lookupViaTrpc,
	checkScan = checkScanViaTrpc,
	handOverAll = handOverAllViaTrpc,
	payAll = payAllViaTrpc,
	cancelCollected = cancelViaTrpc,
	onCashCancelled,
}: HandoverProps) {
	const { t } = useTranslate()
	const [code, setCode] = useState('')
	const [parcels, setParcels] = useState<ReadyParcel[]>([])
	const [recipient, setRecipient] = useState<Recipient | null>(null)
	const [partners, setPartners] = useState<AmbiguousPartner[]>([])
	// Each checked row keeps the code that was scanned for it: Done hands it over
	// by that scan, never by its id (ADR-0032).
	const [selected, setSelected] = useState<Map<string, string>>(new Map())
	const [status, setStatus] = useState<Lookup>('idle')
	const [busy, setBusy] = useState(false)
	const [answer, setAnswer] = useState<Answer | null>(null)
	// When the banner shown now came — its stopwatch counts from here.
	const [shownAt, setShownAt] = useState(0)
	// The Recipient agrees to take a damaged Parcel as it is — the operator's mark, per
	// Parcel, never ticked for them (ticket 42).
	const [damagedAccepted, setDamagedAccepted] = useState<Set<string>>(new Set())
	const [byDocumentPartner, setByDocumentPartner] = useState('')
	// The operator's Shift: `null` until known. A handover needs it open, and theirs.
	const [shift, setShift] = useState<ShiftState | null>(null)
	const [shiftRefusal, setShiftRefusal] = useState<HandoverRefusal | null>(null)
	// «Отменить оплату?» — asked before the cash goes back.
	const [confirmingCancel, setConfirmingCancel] = useState(false)

	const lastLookupRef = useRef<{ value: string; partnerId?: string }>({ value: '' })
	const bearerRef = useRef<HTMLInputElement>(null)
	const scanWrapRef = useRef<HTMLDivElement>(null)
	// Which field the cursor goes to next, selected once the field is enabled again.
	const focusTargetRef = useRef<'bearer' | 'scan' | null>(null)
	const [focusTick, setFocusTick] = useState(0)

	useEffect(() => {
		let live = true
		shiftState()
			.then((res) => live && setShift(res))
			.catch(() => {})
		return () => {
			live = false
		}
	}, [shiftState])

	// Every banner sounds in its tone, and its stopwatch starts.
	const sound = (tone: Tone) => {
		setShownAt(Date.now())
		playTone(tone)
	}

	const showAnswer = (next: Answer) => {
		setAnswer(next)
		sound(answerTone(next))
	}

	// The field is selected, not only focused, so the next scan or code replaces what is there.
	// It waits for the field to be enabled again: both are disabled while a request is out.
	const selectField = useCallback((target: 'bearer' | 'scan') => {
		focusTargetRef.current = target
		setFocusTick((n) => n + 1)
	}, [])

	useEffect(() => {
		const target = focusTargetRef.current
		if (busy || !target) return
		focusTargetRef.current = null
		const input = target === 'bearer' ? bearerRef.current : scanWrapRef.current?.querySelector('input')
		input?.select()
	}, [busy, focusTick])

	// The Recipient's code is entered once; after it and after every act the cursor
	// rests on the parcel scan, so a USB wedge scanner flows straight into checking rows.
	const focusScan = () => selectField('scan')

	const runLookup = async (value: string, partnerId: string | undefined, fresh: boolean) => {
		const trimmed = value.trim()
		if (!trimmed) return
		setBusy(true)
		if (fresh) {
			setAnswer(null)
			setSelected(new Map())
		}
		try {
			const result = await lookup(trimmed, partnerId)
			lastLookupRef.current = { value: trimmed, partnerId }
			// Re-read after a handover: once the last ready parcel went, the lookup finds
			// nothing — the group is still this person's, emptied, never «nothing found».
			if (!fresh && result.outcome === 'not_found') {
				setParcels([])
				setSelected(new Map())
				return
			}
			if (result.outcome === 'ready') {
				setParcels(result.parcels)
				setRecipient(result.recipient)
				setPartners([])
				setStatus('ready')
				if (!fresh) {
					// Keep only selections whose parcel is still ready (handed-over ones drop).
					setSelected((prev) => {
						const next = new Map<string, string>()
						for (const p of result.parcels) {
							const scanned = prev.get(p.parcelId)
							if (scanned !== undefined) next.set(p.parcelId, scanned)
						}
						return next
					})
				} else {
					focusScan()
				}
			} else {
				setParcels([])
				setRecipient(null)
				setPartners(result.outcome === 'ambiguous' ? result.partners : [])
				setByDocumentPartner(
					result.outcome === 'identify_by_document' ? result.partnerName : '',
				)
				setStatus(result.outcome)
				sound(LOOKUP_TONE[result.outcome])
				// No Recipient found: the code is selected, to be typed or scanned again.
				if (fresh) selectField('bearer')
			}
		} finally {
			setBusy(false)
		}
	}

	const refresh = () => {
		const last = lastLookupRef.current
		return runLookup(last.value, last.partnerId, false)
	}

	const handleSearch = (event: FormEvent) => {
		event.preventDefault()
		runLookup(code, undefined, true)
	}

	// The operator chose the partner of a code two partners share.
	const choosePartner = (partnerId: string) => runLookup(lastLookupRef.current.value, partnerId, true)

	/** The Recipient as each scan and handover names it to the server. */
	const scanRecipient = (r: Recipient): ScanRecipient => ({ partnerId: r.partnerId, clientRef: r.clientRef })

	const shiftClosed = shift !== null && !(shift.open && shift.mine)

	// A row's checkbox becomes checked ONLY here — when its parcel's code is scanned
	// and the server confirms it is this Recipient's, on this shelf. A click only
	// unchecks (below).
	const handleScan = async (raw: string) => {
		if (!recipient) return
		const scanned = raw.trim()
		// The banner was the previous scan's: it goes until this one's answer is in.
		setAnswer(null)
		focusScan()
		try {
			const res = await checkScan({ recipient: scanRecipient(recipient), code: scanned })
			if (res.outcome !== 'this_recipient') {
				showAnswer({ kind: 'warn', code: scanned, warnKey: scanWarnKey(res.outcome) ?? '' })
				return
			}
			// A held Parcel is not ready for handover: its scan checks no row (ticket 51).
			const hold = parcels.find((p) => p.parcelId === res.parcelId)?.hold
			if (hold) {
				showAnswer({ kind: 'warn', code: scanned, warnKey: `handover.block_${hold}` })
				return
			}
			showAnswer({ kind: 'checked', code: scanned, parcelId: res.parcelId })
			setSelected((prev) => new Map(prev).set(res.parcelId, scanned))
		} catch {
			showAnswer({ kind: 'warn', code: scanned, warnKey: 'handover.scanFailed' })
		}
	}

	// What is ready for handover: a held Parcel is listed, never counted (ticket 51).
	const readyParcels = useMemo(() => parcels.filter((p) => p.hold === null), [parcels])
	const selectedParcels = useMemo(
		() => readyParcels.filter((p) => selected.has(p.parcelId)),
		[readyParcels, selected],
	)
	// What the Recipient owes for the checked rows.
	const owedTotal = useMemo(
		() => selectedParcels.reduce((total, p) => total + (p.payment.paid ? 0 : p.amountOwed), 0),
		[selectedParcels],
	)
	// The partner's weight of the checked rows; a row without one adds nothing.
	const weightTotal = useMemo(
		() => selectedParcels.reduce((total, p) => total + (p.weightKg ?? 0), 0),
		[selectedParcels],
	)
	// Uzum is 139 Express's (ADR-0049): offered only when every ready parcel's partner takes it.
	const uzumOffered = readyParcels.length > 0 && readyParcels.every((p) => p.uzumAccepted)

	/** The answer to an act: done in full, or a line for each parcel that did not go. */
	const actAnswer = (failed: string, done: string, failures: string[]): Answer =>
		failures.length
			? { kind: 'result', tone: 'refused', title: failed, lines: failures }
			: { kind: 'result', tone: 'ready', title: done, lines: [] }

	/**
	 * What a visit's act answered, in the counter's words: nothing when it went
	 * through, else the one reason it stopped (outside the Shift — said once, with the
	 * way to it — or a line per parcel that stopped it). Nothing was written unless it went through (ADR-0049).
	 */
	const visitFailures = (res: VisitResult): string[] | 'shift' => {
		switch (res.outcome) {
			case 'done':
				return []
			case 'refused':
				if (SHIFT_REFUSALS.includes(res.reason as HandoverRefusal)) {
					setShiftRefusal(res.reason as HandoverRefusal)
					toast.error(t(refusalKey(res.reason as HandoverRefusal) as never))
					setShift({ open: res.reason !== 'no_open_shift_to_hand_over', mine: false })
					return 'shift'
				}
				return [t(refusalKey(res.reason as HandoverRefusal) as never)]
			case 'parcels_refused':
				return res.parcels.length
					? res.parcels.map((p) => `${p.code}: ${t(visitRefusalKey(p.reason) as never)}`)
					: [t('handover.visitRaced')]
		}
	}

	/** The scanned code of each checked parcel. */
	const scannedCodes = () => selectedParcels.map((p) => selected.get(p.parcelId) ?? '')

	// Cash or Uzum: ONE request pays for every checked parcel — the server decides the
	// amounts, skips the ones already `paid?`, and writes all of them or none. The
	// lookup is re-run to refold the payment state.
	const doCollect = async (method: PaymentMethod) => {
		if (!recipient || !hasSelection) return
		setBusy(true)
		setShiftRefusal(null)
		let failures: string[] | 'shift'
		try {
			failures = visitFailures(
				await payAll({
					recipient: scanRecipient(recipient),
					codes: scannedCodes(),
					...(method === 'uzum' ? { method } : {}),
					idempotencyKey: newCommandKey(),
				}),
			)
		} catch {
			failures = [t('payment.collectFailed')]
		}
		await refresh()
		if (failures !== 'shift') {
			showAnswer(
				method === 'uzum'
					? actAnswer(t('handover.uzumFailed'), t('handover.uzumCollected'), failures)
					: actAnswer(t('handover.cashFailed'), t('handover.cashCollected'), failures),
			)
		}
		setBusy(false)
		focusScan()
	}

	// Done: ONE request hands every checked parcel over, each by its own scan — all of
	// them or none. The lookup is re-run so handed-over parcels drop off the list.
	const doDone = async () => {
		if (!recipient || !hasSelection) return
		setBusy(true)
		setShiftRefusal(null)
		let failures: string[] | 'shift'
		try {
			failures = visitFailures(
				await handOverAll({
					recipient: scanRecipient(recipient),
					items: selectedParcels.map((p) => ({
						code: selected.get(p.parcelId) ?? '',
						...(p.markers.includes('damaged') ? { damagedAccepted: damagedAccepted.has(p.parcelId) } : {}),
					})),
				}),
			)
		} catch {
			failures = [t('handover.handoverError')]
		}
		await refresh()
		// Stopped by the Shift: its own notice says why, nothing here.
		if (failures !== 'shift') {
			showAnswer(actAnswer(t('handover.doneFailed'), t('handover.handedOverNotice'), failures))
		}
		setBusy(false)
		// Handed over: the visit is done, the cursor is back on the code for the next person.
		if (failures !== 'shift' && failures.length === 0) selectField('bearer')
		else focusScan()
	}

	// «Снять выбор»: the scans so far are let go; nothing is sent.
	const clearSelection = () => {
		setSelected(new Map())
		setAnswer(null)
	}

	// Cancel: with paid rows selected, cancel each one's counter payment (ADR-0020),
	// confirmed once and with no free-text reason. A payment the partner confirmed
	// is not ours to cancel and is reported. The selection stays, so Cash can be
	// taken again.
	const doCancel = async () => {
		const paid = selectedParcels.filter((p) => p.payment.paid && p.amountOwed > 0)
		setConfirmingCancel(false)
		if (paid.length === 0) return
		setBusy(true)
		const failures: string[] = []
		// Parcels whose partner's double payment became the payment (ADR-0046).
		const activated: string[] = []
		for (const p of paid) {
			if (!p.payment.collected) {
				failures.push(`${firstTrackingCode(p)}: ${t('payment.cancelWrongSource')}`)
				continue
			}
			try {
				const res = await cancelCollected({
					parcelId: p.parcelId,
					idempotencyKey: newCommandKey(),
				})
				const blockKey = cashCancelResultKey(res)
				if (blockKey) failures.push(`${firstTrackingCode(p)}: ${t(blockKey as never)}`)
				// The partner's double payment is the payment now (ADR-0046).
				else if (res.outcome === 'cancelled' && res.partnerPaymentActivated) {
					activated.push(`${firstTrackingCode(p)}: ${t('doublePayment.cashActivated')}`)
				}
			} catch {
				failures.push(`${firstTrackingCode(p)}: ${t('payment.cancelFailed')}`)
			}
		}
		await refresh()
		onCashCancelled?.()
		showAnswer(
			failures.length
				? { kind: 'result', tone: 'refused', title: t('handover.cancelFailed'), lines: [...failures, ...activated] }
				: activated.length
					? { kind: 'result', tone: 'attention', title: t('handover.paymentCancelled'), lines: activated }
					: { kind: 'result', tone: 'ready', title: t('handover.paymentCancelled'), lines: [] },
		)
		setBusy(false)
		focusScan()
	}

	const hasSelection = selectedParcels.length > 0
	const hasPaidSelection = selectedParcels.some((p) => p.payment.paid && p.amountOwed > 0)
	const anyDamaged = parcels.some((p) => p.markers.includes('damaged'))
	// The Shift refusal to show: the server's last word, else what the Shift says.
	const shiftNotice: HandoverRefusal | null =
		shiftRefusal ??
		(shiftClosed
			? shift?.open
				? 'not_shift_operator_to_hand_over'
				: 'no_open_shift_to_hand_over'
			: null)

	const columns = useMemo<ColumnDef<ReadyParcel>[]>(
		() => [
			{
				id: 'check',
				header: '',
				meta: { cellClassName: 'w-10' },
				cell: ({ row: { original: parcel } }) => (
					// Checked only by scanning the parcel's code; a click only unchecks it.
					<Checkbox
						checked={selected.has(parcel.parcelId)}
						disabled={!(parcel.state === 'ON_SHELF' && parcel.hold === null)}
						tabIndex={-1}
						className={selected.has(parcel.parcelId) ? undefined : 'pointer-events-none'}
						aria-label={t('handover.uncheck')}
						onCheckedChange={(v) => {
							if (v !== false) return
							setSelected((prev) => {
								const next = new Map(prev)
								next.delete(parcel.parcelId)
								return next
							})
							selectField('scan')
						}}
						data-testid={`handover-check-${parcel.parcelId}`}
					/>
				),
			},
			{
				id: 'code',
				header: t('handover.colTrackCode'),
				cell: ({ row: { original: parcel } }) => (
					<div className='flex flex-wrap items-center gap-2'>
						<span className='font-mono'>{firstTrackingCode(parcel)}</span>
						{parcel.hold ? (
							<Badge
								variant='destructive'
								data-testid={`handover-hold-${parcel.parcelId}`}
								data-hold={parcel.hold}
							>
								{t(holdKey(parcel.hold) as never)}
							</Badge>
						) : (
							parcel.state !== 'ON_SHELF' && (
								<Badge variant='outline'>{t(`parcelState.${parcel.state}` as never)}</Badge>
							)
						)}
						{parcel.markers.includes('damaged') && (
							<Badge variant='destructive'>{t('parcelMarker.damaged')}</Badge>
						)}
					</div>
				),
			},
			{
				id: 'amount',
				header: t('handover.colAmount'),
				meta: { headClassName: 'text-right', cellClassName: 'text-right tabular-nums' },
				cell: ({ row: { original: parcel } }) => (
					<span
						className={parcel.amountOwed > 0 ? undefined : 'text-muted-foreground'}
						data-testid={`handover-amount-${parcel.parcelId}`}
					>
						{parcel.amountOwed > 0 ? sum(parcel.amountOwed) : t('handover.nothingToPay')}
					</span>
				),
			},
			{
				id: 'weight',
				header: t('handover.colWeight'),
				meta: { headClassName: 'text-right', cellClassName: 'text-right tabular-nums' },
				cell: ({ row: { original: parcel } }) => (
					<span
						className={parcel.weightKg === null ? 'text-muted-foreground' : undefined}
						data-testid={`handover-weight-${parcel.parcelId}`}
					>
						{parcel.weightKg === null ? '—' : kg(parcel.weightKg)}
					</span>
				),
			},
			{
				id: 'payment',
				header: t('handover.colPayment'),
				cell: ({ row: { original: parcel } }) =>
					parcel.payment.paid ? (
						<Badge
							variant='secondary'
							data-testid={`handover-paid-${parcel.parcelId}`}
							data-method={parcel.payment.collected?.method}
						>
							{parcel.payment.collected?.method === 'uzum'
								? t('payment.statusPaidUzum')
								: t('payment.statusPaid')}
						</Badge>
					) : (
						<Badge
							variant='outline'
							className='border-amber-500/60 text-amber-700 dark:text-amber-400'
							data-testid={`handover-unpaid-${parcel.parcelId}`}
						>
							{t('payment.statusUnpaid')}
						</Badge>
					),
			},
			// A damaged Parcel goes only with the Recipient's consent, marked per Parcel (ticket 42).
			...(anyDamaged
				? [
						{
							id: 'damaged',
							header: '',
							cell: ({ row: { original: parcel } }) =>
								parcel.markers.includes('damaged') && (
									<label className='flex items-center gap-2 text-sm'>
										<Checkbox
											checked={damagedAccepted.has(parcel.parcelId)}
											onCheckedChange={(v) =>
												setDamagedAccepted((prev) => {
													const next = new Set(prev)
													if (v === true) next.add(parcel.parcelId)
													else next.delete(parcel.parcelId)
													return next
												})
											}
											data-testid={`handover-damaged-accepted-${parcel.parcelId}`}
										/>
										{t('handover.damagedAccepted')}
									</label>
								),
						} satisfies ColumnDef<ReadyParcel>,
					]
				: []),
		],
		[t, selected, damagedAccepted, anyDamaged, selectField],
	)
	const { table } = useDataTable({
		data: parcels,
		columns,
		rowCount: parcels.length,
		manualPagination: false,
		manualSorting: false,
		manualFiltering: false,
		enableRowSelection: false,
		getRowId: (parcel) => parcel.parcelId,
		initialState: { pagination: { pageIndex: 0, pageSize: 1000 } },
	})

	return (
		<PageFrame title='nav.handover'>
			{shiftNotice && (
				<Alert
					variant='destructive'
					className='flex items-center gap-3'
					data-testid='handover-shift-refusal'
					data-reason={shiftNotice}
				>
					<AlertTriangle />
					<AlertTitle className='flex-1'>{t(refusalKey(shiftNotice) as never)}</AlertTitle>
					{shiftLink}
				</Alert>
			)}

			{/* Who is at the counter — the code they show — and, beside it, the parcel scan. */}
			<section className='flex flex-col gap-3'>
				<div className='flex flex-wrap items-start gap-x-8 gap-y-3'>
					<form className='flex items-center gap-2' onSubmit={handleSearch} data-testid='bearer-form'>
						<Input
							ref={bearerRef}
							id='bearer-code'
							data-testid='bearer-input'
							type='search'
							className='w-72'
							autoFocus
							autoComplete='off'
							value={code}
							disabled={busy}
							placeholder={t('handover.bearerPlaceholder')}
							aria-label={t('handover.bearerPlaceholder')}
							onChange={(event) => setCode(event.target.value)}
						/>
						<Button type='submit' data-testid='bearer-search' disabled={busy || !code.trim()}>
							{t('handover.search')}
						</Button>
					</form>
					<div ref={scanWrapRef} className='w-[20.5rem]'>
						{/* Remounted for each Recipient found, so the cursor lands on it. */}
						<ScanInput
							key={recipient && status === 'ready' ? `${recipient.partnerId}:${recipient.clientRef}` : 'idle'}
							onScan={handleScan}
							withSubmit={false}
							disabled={busy || status !== 'ready'}
						/>
					</div>
				</div>

				{status === 'not_found' && (
					<ToneBanner
						tone={LOOKUP_TONE.not_found}
						testId='handover-not-found'
						since={shownAt}
						title={t('handover.lookupNotFound')}
						detail={t('handover.notFound')}
					/>
				)}
				{status === 'identify_by_document' && (
					<ToneBanner
						tone={LOOKUP_TONE.identify_by_document}
						testId='handover-identify-by-document'
						since={shownAt}
						title={t('handover.lookupByDocument')}
						detail={t('handover.identifyByDocument', { partner: byDocumentPartner })}
					/>
				)}
				{status === 'wrong_point' && (
					<ToneBanner
						tone={LOOKUP_TONE.wrong_point}
						testId='handover-wrong-point'
						since={shownAt}
						title={t('handover.lookupWrongPoint')}
						detail={t('handover.wrongPoint')}
					/>
				)}
				{status === 'ambiguous' && (
					<ToneBanner
						tone={LOOKUP_TONE.ambiguous}
						testId='handover-ambiguous'
						since={shownAt}
						title={t('handover.lookupAmbiguous')}
						detail={t('handover.choosePartner')}
					>
						<div className='mt-2 flex flex-wrap gap-2'>
							{partners.map((p) => (
								<Button
									key={p.partnerId}
									type='button'
									variant='outline'
									disabled={busy}
									data-testid={`handover-partner-${p.partnerId}`}
									onClick={() => choosePartner(p.partnerId)}
								>
									{p.name} ({p.prefix})
								</Button>
							))}
						</div>
					</ToneBanner>
				)}
			</section>

			{/* Their parcels: the summary and the acts on top, the last answer, then the rows — each checked by its scan. */}
			{status === 'ready' && recipient && (
				<div className='flex flex-col gap-4' data-testid='handover-group'>
					{/* A double payment on a Parcel here (ADR-0046): the cash goes back, not out. */}
					{parcels
						.filter((p) => p.doublePayment)
						.map((p) => (
							<Alert
								key={`double-${p.parcelId}`}
								variant='destructive'
								data-testid={`handover-double-payment-${p.parcelId}`}
							>
								<AlertTriangle />
								<AlertTitle>{t('handover.doublePaymentTitle', { code: firstTrackingCode(p) })}</AlertTitle>
								<AlertDescription>
									{t('doublePayment.banner', {
										code: firstTrackingCode(p),
										partner:
											p.doublePayment!.currency === 'UZS'
												? t('payment.som', { amount: sum(p.doublePayment!.amount) })
												: `${sum(p.doublePayment!.amount)} ${p.doublePayment!.currency}`,
										cash: t('payment.som', { amount: sum(p.doublePayment!.cashAmount) }),
									})}
								</AlertDescription>
							</Alert>
						))}

					<div className='flex flex-wrap items-end justify-between gap-4'>
						<Stats testId='handover-summary'>
							<Stat
								label={t('handover.summarySelected')}
								valueTestId='handover-summary-selected'
								value={
									<>
										{selectedParcels.length}
										<span className='font-normal text-muted-foreground'> {t('handover.of')} </span>
										{readyParcels.length}
									</>
								}
							/>
							<Stat
								label={t('handover.summaryWeight')}
								valueTestId='handover-summary-weight'
								value={kg(weightTotal)}
							/>
							<Stat
								label={t('handover.summaryTotal')}
								valueTestId='handover-summary-total'
								value={sum(owedTotal)}
							/>
						</Stats>

						<div className='flex flex-wrap items-center justify-end gap-3'>
							{hasSelection && (
								<DropdownMenu modal={false}>
									<DropdownMenuTrigger asChild>
										<Button
											variant='outline'
											size='icon'
											disabled={busy}
											aria-label={t('handover.moreActions')}
											data-testid='handover-more'
										>
											<MoreHorizontal />
										</Button>
									</DropdownMenuTrigger>
									<DropdownMenuContent
										align='end'
										className='w-56'
										onCloseAutoFocus={(event) => {
											event.preventDefault()
											focusScan()
										}}
									>
										<DropdownMenuItem data-testid='handover-clear' onSelect={clearSelection}>
											<X />
											{t('handover.clearSelection')}
										</DropdownMenuItem>
										{hasPaidSelection && (
											<DropdownMenuItem
												variant='destructive'
												data-testid='handover-cancel'
												onSelect={() => setConfirmingCancel(true)}
											>
												<Banknote />
												{t('payment.cancelSubmit')}
											</DropdownMenuItem>
										)}
									</DropdownMenuContent>
								</DropdownMenu>
							)}
							{owedTotal > 0 && uzumOffered && (
								<Button
									type='button'
									variant='outline'
									data-testid='handover-uzum'
									disabled={busy}
									onClick={() => doCollect('uzum')}
								>
									{t('payment.collectUzum')}
								</Button>
							)}
							{owedTotal > 0 && (
								<Button
									type='button'
									data-testid='handover-cash'
									disabled={busy}
									onClick={() => doCollect('cash')}
								>
									{t('payment.collectSubmit')}
								</Button>
							)}
							<Button
								type='button'
								data-testid='handover-done'
								// Handed over only once every checked parcel is paid (or owes nothing).
								disabled={busy || !hasSelection || owedTotal > 0}
								onClick={doDone}
							>
								{t('handover.done')}
							</Button>
						</div>
					</div>

					{answer && (
						<AnswerBanner
							answer={answer}
							since={shownAt}
							parcels={parcels}
							selected={selectedParcels.length}
							ready={readyParcels.length}
						/>
					)}

					<DataTable
						table={table}
						withPagination={false}
						maxHeightClassName='max-h-[420px]'
						emptyText={<span data-testid='handover-empty'>{t('handover.nothingReady')}</span>}
						row={{
							attributes: (parcel) => ({
								'data-testid': `handover-row-${parcel.parcelId}`,
								'data-selected': String(selected.has(parcel.parcelId)),
								'data-state': selected.has(parcel.parcelId) ? 'selected' : undefined,
							}),
						}}
					/>
				</div>
			)}

			{footer}

			<AlertDialog open={confirmingCancel} onOpenChange={(open) => !busy && setConfirmingCancel(open)}>
				<AlertDialogContent
					data-testid='handover-cancel-dialog'
					onCloseAutoFocus={(event) => {
						event.preventDefault()
						focusScan()
					}}
				>
					<AlertDialogHeader>
						<AlertDialogTitle>{t('payment.cancelAsk')}</AlertDialogTitle>
						<AlertDialogDescription>{t('payment.cancelConfirm')}</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel data-testid='handover-cancel-back'>{t('payment.cancelBack')}</AlertDialogCancel>
						<Button variant='destructive' data-testid='handover-cancel-confirm' disabled={busy} onClick={doCancel}>
							{t('payment.cancelSubmit')}
						</Button>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</PageFrame>
	)
}

/** The tone of an answer: a parcel checked is green; a scan not found or not checked yellow; another refusal red. */
function answerTone(answer: Answer): Tone {
	switch (answer.kind) {
		case 'checked':
			return 'ready'
		case 'warn':
			return answer.warnKey === 'handover.scanNotFound' || answer.warnKey === 'handover.scanFailed'
				? 'attention'
				: 'refused'
		case 'result':
			return answer.tone
	}
}

/** The answer above the parcels: the last scan, or what the last act did. */
function AnswerBanner({
	answer,
	since,
	parcels,
	selected,
	ready,
}: {
	answer: Answer
	/** When it came (epoch ms) — its stopwatch counts from here. */
	since: number
	parcels: ReadyParcel[]
	selected: number
	ready: number
}) {
	const { t } = useTranslate()
	switch (answer.kind) {
		case 'checked': {
			const parcel = parcels.find((p) => p.parcelId === answer.parcelId)
			const payment = !parcel
				? null
				: parcel.payment.paid
					? t('payment.statusPaid')
					: parcel.amountOwed > 0
						? t('handover.scanToPay', { amount: sum(parcel.amountOwed) })
						: t('handover.nothingToPay')
			return (
				<ToneBanner
					tone='ready'
					testId='handover-scan-answer'
					since={since}
					attributes={{ 'data-outcome': 'this_recipient' }}
					title={t('handover.scanCheckedTitle', { code: answer.code })}
					detail={[payment, t('handover.scanSelectedCount', { selected: String(selected), ready: String(ready) })]
						.filter(Boolean)
						.join(' · ')}
				/>
			)
		}
		case 'warn': {
			const notFound = answer.warnKey === 'handover.scanNotFound'
			const failed = answer.warnKey === 'handover.scanFailed'
			return (
				<ToneBanner
					tone={answerTone(answer)}
					testId='handover-scan-answer'
					since={since}
					attributes={{ 'data-outcome': answer.warnKey }}
					title={
						failed
							? t('handover.scanFailedTitle')
							: t(notFound ? 'handover.scanNotFoundTitle' : 'handover.scanRefusedTitle', { code: answer.code })
					}
					detail={
						<span data-testid='handover-scan-warn' data-warn={answer.warnKey}>
							{t(answer.warnKey as never)}
						</span>
					}
				/>
			)
		}
		case 'result':
			return answer.lines.length === 0 ? (
				<ToneBanner
					tone={answer.tone}
					testId='handover-act-answer'
					titleTestId='handover-result'
					title={answer.title}
					since={since}
				/>
			) : (
				<ToneBanner
					tone={answer.tone}
					testId='handover-act-answer'
					title={answer.title}
					since={since}
					detail={
						<div className='whitespace-pre-line' data-testid='handover-result'>
							{answer.lines.join('\n')}
						</div>
					}
				/>
			)
	}
}
